/**
 * Core Trivia Game Types
 *
 * This file defines all TypeScript types and interfaces for the Trivia/Quiz
 * game implementation. Supports multiple choice and true/false questions
 * across 6 categories with timed scoring.
 */

// ============================================================================
// Basic Trivia Types
// ============================================================================

/** Trivia question categories */
export type TriviaCategory =
  | 'Science'
  | 'History'
  | 'Geography'
  | 'Entertainment'
  | 'Sports'
  | 'Technology';

/** Question types */
export type TriviaQuestionType = 'multiple-choice' | 'true-false';

/** AI difficulty levels */
export type TriviaDifficulty = 'easy' | 'medium' | 'hard';

/** Game phase */
export type TriviaPhase =
  | 'idle'
  | 'category-select'
  | 'question'
  | 'answer-reveal'
  | 'round-end'
  | 'game-over';

// ============================================================================
// Question Types
// ============================================================================

/** A single trivia question */
export interface TriviaQuestion {
  id: string;
  category: TriviaCategory;
  type: TriviaQuestionType;
  difficulty: 'easy' | 'medium' | 'hard';
  question: string;
  options: string[];
  correctAnswer: string;
  explanation?: string;
}

// ============================================================================
// Player Types
// ============================================================================

/** A trivia player */
export interface TriviaPlayer {
  id: string;
  name: string;
  isAI: boolean;
  score: number;
  streak: number;
  correctAnswers: number;
  totalAnswers: number;
  averageResponseTime: number;
}

// ============================================================================
// Answer Types
// ============================================================================

/** A player's answer to a question */
export interface TriviaAnswer {
  playerId: string;
  questionId: string;
  selectedOption: string;
  isCorrect: boolean;
  responseTime: number; // milliseconds
  pointsEarned: number;
  timestamp: number;
}

// ============================================================================
// Round Types
// ============================================================================

/** A round of trivia (10 questions) */
export interface TriviaRound {
  roundNumber: number;
  questions: TriviaQuestion[];
  answers: TriviaAnswer[];
  currentQuestionIndex: number;
  startedAt: number;
}

// ============================================================================
// Game State Types
// ============================================================================

/** Complete trivia game state */
export interface TriviaGameState {
  gameId: string;
  phase: TriviaPhase;
  players: TriviaPlayer[];
  currentRound: TriviaRound | null;
  currentQuestion: TriviaQuestion | null;
  questionStartTime: number | null;
  timeRemaining: number; // seconds
  questionsPerRound: number;
  timePerQuestion: number; // seconds (15)
  maxPointsPerQuestion: number; // 1000
  selectedCategories: TriviaCategory[];
  roundsPlayed: number;
  isGameOver: boolean;
}

// ============================================================================
// Game Settings Types
// ============================================================================

/** Game settings for mode selection */
export interface TriviaGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: TriviaDifficulty;
  categories?: TriviaCategory[];
  questionsPerRound?: number;
  timePerQuestion?: number;
}

// ============================================================================
// Game Result Types
// ============================================================================

/** Game result for statistics tracking */
export interface TriviaGameResult {
  gameId: string;
  mode: TriviaGameSettings['mode'];
  difficulty?: TriviaDifficulty;
  winner?: string;
  isDraw: boolean;
  players: TriviaPlayer[];
  totalQuestions: number;
  duration: number; // seconds
  completedAt: number;
}

// ============================================================================
// Statistics Types
// ============================================================================

/** Game statistics for a specific category */
export interface TriviaStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  totalQuestionsAnswered: number;
  correctAnswerRate: number;
}

/** Complete Trivia statistics */
export interface TriviaStatistics {
  userId?: string;
  singlePlayer: {
    easy: TriviaStats;
    medium: TriviaStats;
    hard: TriviaStats;
  };
  localMultiplayer: TriviaStats;
  onlineMultiplayer: TriviaStats;
  totalGames: number;
  totalPlayTime: number; // seconds
  categoryStats: Record<TriviaCategory, { correct: number; total: number }>;
  bestStreak: number;
  highestScore: number;
}

// ============================================================================
// Constants
// ============================================================================

/** All available categories */
export const ALL_CATEGORIES: TriviaCategory[] = [
  'Science',
  'History',
  'Geography',
  'Entertainment',
  'Sports',
  'Technology',
];

/** Default questions per round */
export const DEFAULT_QUESTIONS_PER_ROUND = 10;

/** Default time per question in seconds */
export const DEFAULT_TIME_PER_QUESTION = 15;

/** Maximum points per question */
export const MAX_POINTS_PER_QUESTION = 1000;

/** Minimum points for a correct answer (at time limit) */
export const MIN_POINTS_PER_QUESTION = 100;

/** Streak bonus multiplier (consecutive correct answers) */
export const STREAK_BONUS_MULTIPLIER = 0.1; // 10% bonus per streak level
